import { redirectToHome, getAbsoluteUrl } from "utils/method";
import cookie from "utils/cookie";
import sessionStorage from "utils/sessionStorage";

const BIND_THIRD_KEY = "googleBindThird";
const SET_PASSWORD_KEY = "googleSetPassword";

/**
 * Mock Google Token 场景开关：0=走真实接口，1=需关联账号(bind-third)，2=需设置密码(set-password)，3=直接登录成功
 * 测试时改为 1/2/3，测完改回 0
 */
export const MOCK_GOOGLE_TOKEN_SCENARIO = 0;

/** Mock 三种 Google 登录接口返回，用于本地测试 */
export function getMockGoogleTokenResponse(scenario) {
  const mock = {
    1: {
      needBindThird: true,
      loginStatus: 3,
      bindEmail: "tes***@gmail.com",
      thirdType: "google",
      accessToken: null,
    },
    2: {
      needSetPassword: true,
      accessToken: "mock_access_token_set_password",
      thirdType: "google",
    },
    3: {
      accessToken: "mock_access_token_login_ok",
      needBindThird: false,
      needSetPassword: false,
    },
  };
  return mock[scenario] || null;
}

/** 保存需绑定第三方时的参数，供 bind-third 页使用 */
export function saveBindThirdPayload(idToken, bindEmail, thirdType) {
  sessionStorage.set(BIND_THIRD_KEY, { idToken, bindEmail, thirdType });
}

/** 仅读取绑定第三方参数（不清除），用于 bind-third 页首屏校验，避免 effect 二次执行时误判 */
export function getBindThirdPayload() {
  return sessionStorage.get(BIND_THIRD_KEY) || null;
}

/** 读取并清除绑定第三方参数（提交绑定或离开时调用） */
export function takeBindThirdPayload() {
  const payload = sessionStorage.get(BIND_THIRD_KEY);
  if (payload) sessionStorage.remove(BIND_THIRD_KEY);
  return payload;
}

/** 第三方类型归一化为小写 "google" | "apple"，供 set-password 页判断展示名 */
function normalizeThirdType(val) {
  if (!val || typeof val !== "string") return "";
  const v = val.toLowerCase();
  return v === "apple" ? "apple" : v === "google" ? "google" : "";
}

/** 保存需设置密码时的 token 与第三方类型，供 set-password 页使用；接口可能不返回 thirdType，用 sourceThirdType 兜底 */
export function saveSetPasswordToken(data, sourceThirdType) {
  sessionStorage.set("token", data.accessToken);
  cookie.set("token", data.accessToken);
  sessionStorage.set(SET_PASSWORD_KEY, true);
  const raw = data.thirdType || data.type || data.thirdName || sourceThirdType || "google";
  sessionStorage.set("googleSetPasswordThirdType", normalizeThirdType(raw) || normalizeThirdType(sourceThirdType) || "google");
}

/**
 * 处理 Google/Apple 登录接口返回：需绑定则跳转 bind-third，需设置密码则跳转 set-password，否则走登录成功
 * @param sourceThirdType 当前调用的第三方类型 "google" | "apple"，接口未返回 thirdType 时用此值兜底
 */
export function handleThirdLoginResponse(data, router, idToken, sourceThirdType = "google") {
  const rawThird = data?.thirdType || data?.type || data?.thirdName || sourceThirdType || "google";
  const thirdType = normalizeThirdType(rawThird) || normalizeThirdType(sourceThirdType) || "google";
  const condBindThird = !!(data && data.needBindThird && (data.loginStatus === 3 || !data.accessToken));
  const condSetPassword = !!(data && data.needSetPassword && data.accessToken);
  const condLoginOk = !!(data && data.accessToken);

  // needBindThird → 绑定第三方（输入登录密码）→ /bind-third，调 bindThirdLogin；needSetPassword → 设置密码 → /set-password，调 setLoginPassword
  if (condBindThird) {
    saveBindThirdPayload(idToken, data.bindEmail || "", thirdType);
    const pathForUrl = "/bind-third";
    const pathForRouter = "/accounts/bind-third";
    
    if (typeof location !== "undefined")
      location.href = getAbsoluteUrl({ pathname: pathForUrl, locale: router.locale, query: router.query });
    else router.push({ pathname: pathForRouter, query: router.query });
    return;
  }
  if (condSetPassword) {
    saveSetPasswordToken(data, sourceThirdType);
    const pathForUrl = "/set-password";
    const pathForRouter = "/accounts/set-password";
    if (typeof location !== "undefined")
      location.href = getAbsoluteUrl({ pathname: pathForUrl, locale: router.locale, query: router.query });
    else router.push({ pathname: pathForRouter, query: router.query });
    return;
  }
  if (condLoginOk) {
    console.log("[handleThirdLoginResponse] 执行分支: 登录成功 loginSuccessAndSkip");
    loginSuccessAndSkip(data, router);
    return;
  }
  console.warn("[handleThirdLoginResponse] 未进入任何分支，未跳转。请检查后端返回的 needBindThird/loginStatus/accessToken/needSetPassword 是否符合预期。");
}

export function loginSuccessAndSkip(data, router) {
  if (data.loginStatus === 5) {
    sessionStorage.set("mfaId", data.mfaId);
    if (typeof location !== "undefined")
      return (location.href = getAbsoluteUrl({
        pathname: "/login/security",
        locale: router.locale,
        query: router.query,
      }));
    router.push({
      pathname: "/login/security",
      query: router.query,
    });
    return;
  }

  //登录成功
  sessionStorage.set("token", data.accessToken);
  cookie.set("token", data.accessToken);

  try {
    sensors.login(data.userVO.userId);
  } catch (e) {
    //
  }

  if (
    data.userVO &&
    !data.userVO.regCountryId &&
    !data.userVO.kycAuthCountryId
  ) {
    if (typeof location !== "undefined")
      location.replace(
        getAbsoluteUrl({
          pathname: "/country",
          locale: router.locale,
          query: router.query,
        })
      );
    else
      router.replace({
        pathname: "/country",
        query: router.query,
      });
  } else {
    // messenger 通知 zendesk (Replaced by LiveHelp100)
    // if (window.zE) {
    //   console.log("window.zE 9999");
    //   window.zE("messenger", "loginUser", function (callback) {
    //     callback(data.accessToken || "");
    //   });
    // }
    // 登录成功后统一跳转首页，避免返回键回到登录页
    redirectToHome(router);
    // redirectBackUrl(router); // 原逻辑：按 backurl 或默认地址跳转，需恢复时可改回
  }
}
